import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Download, Play, Plus, Trash2 } from 'lucide-react';
import { WindowBreakpoints } from '@/hooks/useWindowResponsive';
import { DynamicIcon } from '@/components/ui/dynamic-icon';
import { isSystemApp } from '@/lib/apps';
import { ElegantProgressBar } from '@/components/ui/elegant-progress-bar';
import { motion, AnimatePresence } from 'framer-motion';

interface App {
  id: string;
  name: string;
  description?: string;
  category: string;
  icon_name: string;
  component_type: string;
}

interface AppStoreAppCardProps {
  app: App;
  onAppAction: (action: string, appId: string, appName: string) => void;
  appStore: any;
  variant?: 'grid' | 'list';
  responsive?: WindowBreakpoints & {
    isNarrow: boolean;
    isMedium: boolean;
  };
  installationProgress?: Map<string, number>;
}

// Helper function to get the correct icon component - now uses DynamicIcon
const getIconName = (iconName: string) => {
  // Convert PascalCase to kebab-case for Lucide icons
  return iconName
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .toLowerCase();
};

const getCategoryColor = (category: string) => {
  switch (category) {
    case 'productivity': return 'bg-zinc-800/50 text-amber-200/90 border-amber-900/30';
    case 'communication': return 'bg-zinc-800/50 text-amber-200/90 border-amber-900/30';
    case 'utilities': return 'bg-zinc-800/50 text-amber-200/90 border-amber-900/30';
    case 'system': return 'bg-zinc-800/50 text-amber-200/90 border-amber-900/30';
    default: return 'bg-zinc-800/50 text-amber-200/90 border-amber-900/30';
  }
};

export function AppStoreAppCard({ app, onAppAction, appStore, variant = 'grid', responsive, installationProgress }: AppStoreAppCardProps) {
  const iconName = getIconName(app.icon_name);
  const isInstalled = appStore.isAppInstalled(app.id);
  const isInDock = appStore.isAppInDock(app.id);
  const isAddingToDock = appStore.isAddingToDock ? appStore.isAddingToDock(app.id) : false;
  const isSystem = isSystemApp(app);
  const currentProgress = installationProgress?.get(app.id);

  if (variant === 'list') {
    return (
      <div className="flex items-center gap-4 p-6 rounded-xl bg-card/30 backdrop-blur-sm border border-border/50 hover:bg-card/50 transition-all duration-200 hover:scale-[1.01]">
        <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-accent/20 to-accent/30 border border-accent/30 flex items-center justify-center flex-shrink-0">
          <DynamicIcon name={iconName} className="h-8 w-8 text-accent" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold text-foreground truncate">{app.name}</h3>
            <Badge 
              variant="outline" 
              className={cn("text-xs", getCategoryColor(app.category))}
            >
              {app.category}
            </Badge>
          </div>
          <p className="text-muted-foreground text-sm line-clamp-2">
            {app.description}
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          {!isInstalled ? (
            <Button
              onClick={() => onAppAction('install', app.id, app.name)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 font-semibold rounded-lg"
            >
              Get
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => onAppAction('open', app.id, app.name)}
                className="px-6 font-semibold rounded-lg border-2"
              >
                Open
              </Button>
              {!isInDock && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => onAppAction('addToDock', app.id, app.name)}
                  className="rounded-lg border-2"
                  disabled={isAddingToDock}
                >
                  <Plus className={`h-4 w-4 ${isAddingToDock ? 'animate-pulse' : ''}`} />
                </Button>
              )}
              {!isSystem && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => onAppAction('uninstall', app.id, app.name)}
                  className="rounded-lg border-2 hover:border-destructive hover:text-destructive"
                  title="Uninstall app"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </>
          )}
        </div>
        
        {isInstalled && (
          <div className={cn(
            "flex items-center gap-1 text-xs text-muted-foreground",
            responsive?.isNarrow ? "justify-center" : "flex-shrink-0 ml-2"
          )}>
            <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
            {isInDock ? "In Dock" : "Installed"}
          </div>
        )}
      </div>
    );
  }

  return (
    <motion.div 
      className={cn(
        "group relative overflow-hidden rounded-xl backdrop-blur-sm transition-all duration-300",
        "bg-gradient-to-br from-zinc-900/90 via-zinc-800/50 to-zinc-900/80",
        "border border-zinc-700/40",
        "hover:shadow-xl hover:shadow-amber-900/20",
        "hover:border-amber-900/40",
        "hover:scale-[1.02]",
        responsive?.isNarrow && "rounded-lg"
      )}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      <div className={responsive?.isNarrow ? "p-4" : "p-5"}>
        {/* Icon and Status Row */}
        <div className={cn(
          "flex items-start justify-between",
          responsive?.isNarrow ? "mb-3" : "mb-4"
        )}>
          <div className={cn(
            "rounded-xl backdrop-blur-sm flex items-center justify-center",
            "bg-gradient-to-br from-zinc-800/60 to-zinc-900/80",
            "border border-zinc-700/40",
            "group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-amber-900/30 group-hover:border-amber-900/50 transition-all duration-300",
            responsive?.isNarrow ? "w-12 h-12" : "w-14 h-14"
          )}>
            <DynamicIcon 
              name={iconName}
              className={cn(
                "text-amber-200/80",
                responsive?.isNarrow ? "h-6 w-6" : "h-7 w-7"
              )} 
            />
          </div>
          
          {isInstalled && (
            <div className="flex items-center gap-1.5 text-xs text-zinc-400 font-light">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-400/80" />
              {isInDock ? "Dock" : "Installed"}
            </div>
          )}
        </div>
        
        {/* App Info */}
        <div className={responsive?.isNarrow ? "mb-3" : "mb-4"}>
          <h3 className={cn(
            "font-light text-zinc-100 line-clamp-1 mb-2 tracking-wide",
            responsive?.isNarrow ? "text-sm" : "text-base"
          )}>
            {app.name}
          </h3>
          <Badge 
            variant="outline" 
            className={cn(
              "text-xs mb-2 px-2 py-0.5 font-light",
              getCategoryColor(app.category)
            )}
          >
            {app.category}
          </Badge>
          <p className={cn(
            "text-zinc-400 line-clamp-2 leading-relaxed font-light",
            responsive?.isNarrow ? "text-xs" : "text-sm"
          )}>
            {app.description}
          </p>
        </div>
        
        {/* Action Buttons with Progress States */}
        <div className="flex gap-2">
          <AnimatePresence mode="wait">
            {currentProgress !== undefined ? (
              // Installing State: Show Progress Bar
              <motion.div
                key="progress"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="flex-1"
              >
                <ElegantProgressBar progress={currentProgress} />
              </motion.div>
            ) : !isInstalled ? (
              // Not Installed: Show Get Button
              <motion.div
                key="get"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="flex-1"
              >
                <Button
                  size="sm"
                  onClick={() => onAppAction('install', app.id, app.name)}
                  className={cn(
                    "w-full font-light rounded-lg transition-all duration-300",
                    "bg-gradient-to-r from-amber-900/30 via-amber-800/20 to-amber-900/30",
                    "text-amber-200 border border-amber-900/50",
                    "hover:from-amber-800/40 hover:via-amber-700/30 hover:to-amber-800/40",
                    "hover:border-amber-700/60 hover:text-amber-100",
                    "hover:shadow-lg hover:shadow-amber-900/30 hover:scale-[1.02]",
                    "active:scale-[0.98]"
                  )}
                >
                  <Download className="mr-2 h-3.5 w-3.5" />
                  Get
                </Button>
              </motion.div>
            ) : (
              // Installed: Show Open Button
              <motion.div
                key="open"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="flex-1"
              >
                <Button
                  size="sm"
                  onClick={() => onAppAction('open', app.id, app.name)}
                  className={cn(
                    "w-full font-light rounded-lg transition-all duration-300",
                    "bg-gradient-to-r from-zinc-800 via-zinc-700 to-zinc-800",
                    "text-amber-100 border border-zinc-600/50",
                    "hover:from-zinc-700 hover:via-zinc-600 hover:to-zinc-700",
                    "hover:border-amber-900/50 hover:text-amber-50",
                    "hover:shadow-xl hover:shadow-amber-900/20 hover:scale-[1.02]",
                    "active:scale-[0.98]"
                  )}
                >
                  <Play className="mr-2 h-3.5 w-3.5" />
                  Open
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Additional Actions (only show when installed and not installing) */}
          {isInstalled && currentProgress === undefined && (
            <>
              {!isInDock && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onAppAction('addToDock', app.id, app.name)}
                  className={cn(
                    "rounded-lg border-zinc-700/50 text-zinc-400 bg-zinc-900/30",
                    "hover:bg-zinc-800/50 hover:border-amber-900/40 hover:text-amber-200",
                    "transition-all duration-200"
                  )}
                  disabled={isAddingToDock}
                >
                  <Plus className={`h-3.5 w-3.5 ${isAddingToDock ? 'animate-pulse' : ''}`} />
                </Button>
              )}
              {!isSystem && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onAppAction('uninstall', app.id, app.name)}
                  className={cn(
                    "rounded-lg border-zinc-700/50 text-zinc-400 bg-zinc-900/30",
                    "hover:bg-red-950/50 hover:border-red-900/50 hover:text-red-400",
                    "transition-all duration-200"
                  )}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}